import { useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const formats = [
  { label: 'Bold', before: '**', after: '**', sample: 'bold text' },
  { label: 'Italic', before: '*', after: '*', sample: 'italic text' },
  { label: 'Heading', before: '## ', sample: 'Heading', block: true },
  { label: 'List', before: '- ', sample: 'List item', block: true },
  { label: 'Checklist', before: '- [ ] ', sample: 'Task', block: true },
  { label: 'Quote', before: '> ', sample: 'Quote', block: true },
  { label: 'Code', before: '`', after: '`', sample: 'code' },
  { label: 'Link', before: '[', after: '](https://example.com)', sample: 'link text' },
];

export default function MarkdownEditor({ value, onChange }) {
  const [preview, setPreview] = useState(false);
  const inputRef = useRef(null);

  const applyFormat = ({ before, after = '', sample, block }) => {
    const input = inputRef.current;
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const selected = value.slice(start, end) || sample;
    const prefix = block && start > 0 && value[start - 1] !== '\n' ? '\n' : '';
    const formatted = block ? selected.split('\n').map((line) => before + line).join('\n') : before + selected + after;
    onChange(value.slice(0, start) + prefix + formatted + value.slice(end));
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(start + prefix.length + before.length, start + prefix.length + formatted.length - after.length);
    });
  };

  return (
    <section className="markdown-editor" aria-label="Note content">
      <div className="markdown-toolbar">
        <div className="markdown-modes" role="group" aria-label="Editor mode">
          <button type="button" aria-pressed={!preview} onClick={() => setPreview(false)}>Write</button>
          <button type="button" aria-pressed={preview} onClick={() => setPreview(true)}>Preview</button>
        </div>
        {!preview && <div className="markdown-formats" role="group" aria-label="Text formatting">
          {formats.map((format) => <button key={format.label} type="button" onClick={() => applyFormat(format)}>{format.label}</button>)}
        </div>}
      </div>
      {preview ? (
        <div className="markdown-preview" aria-label="Markdown preview">
          {value.trim() ? <Markdown remarkPlugins={[remarkGfm]}>{value}</Markdown> : <p className="markdown-hint">Nothing to preview yet.</p>}
        </div>
      ) : (
        <textarea
          ref={inputRef}
          className="note-content-input"
          aria-label="Note text in Markdown"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && ['b', 'i'].includes(event.key.toLowerCase())) {
              event.preventDefault();
              applyFormat(formats[event.key.toLowerCase() === 'b' ? 0 : 1]);
            }
          }}
          placeholder="Write your note here… Markdown supported."
        />
      )}
      <p className="markdown-hint">Markdown: **bold**, *italic*, # heading, - list, - [ ] task. Use Preview to see formatting.</p>
    </section>
  );
}
